import { Component } from '@theme/component';
import { fetchConfig, onAnimationEnd, preloadImage } from '@theme/utilities';
import { ThemeEvents, CartErrorEvent, CartAddEvent } from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { morph } from '@theme/morph';

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

export class AddToCartComponent extends Component {
  requiredRefs = ['addToCartButton'];
  #animationTimeout;
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerenter', this.#preloadImage, { passive: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    this.removeEventListener('pointerenter', this.#preloadImage);
  }

  disable() {
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = true;
      this.refs.addToCartButton.setAttribute('aria-disabled', 'true');
      this.refs.addToCartButton.classList.add('is-loading');
    }
  }

  enable() {
    if (this.refs.addToCartButton) {
      this.refs.addToCartButton.disabled = false;
      this.refs.addToCartButton.setAttribute('aria-disabled', 'false');
      this.refs.addToCartButton.classList.remove('is-loading');
    }
  }

  handleClick(event) {
    const form = this.closest('form');
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === 'true';
    const isQuickAdd = !!event.target.closest('.quick-add-modal');

    if (animationEnabled && !isQuickAdd) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;
    if (!image) return;
    preloadImage(image);
  };

  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector('.header-actions__cart-icon');
    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = document.createElement('fly-to-cart');
    flyToCartElement.style.setProperty('background-image', `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;
    document.body.appendChild(flyToCartElement);
  }

  animateAddToCart() {
    const { addToCartButton } = this.refs;
    if (!addToCartButton) return;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains('atc-added')) {
      addToCartButton.classList.add('atc-added');
    }

    this.#animationTimeout = window.setTimeout(() => {
      this.#cleanupTimeout = window.setTimeout(() => {
        addToCartButton.classList.remove('atc-added');
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get('add-to-cart-component')) {
  customElements.define('add-to-cart-component', AddToCartComponent);
}

class ProductFormComponent extends Component {
  requiredRefs = ['variantId', 'liveRegion'];
  #abortController = new AbortController();
  #timeout;
  #isSubmitting = false;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest('.shopify-section, dialog, product-card');

    if (target) {
      target.addEventListener(ThemeEvents.variantUpdate, this.onVariantUpdate.bind(this), { signal });
      target.addEventListener(ThemeEvents.variantSelected, this.onVariantSelected.bind(this), { signal });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();

    if (this.#timeout) clearTimeout(this.#timeout);
  }

  async handleSubmit(event) {
    console.log('handleSubmit fired', this, window.location.pathname);

    event.preventDefault();
    event.stopImmediatePropagation();
    event.stopPropagation();

    const { addToCartTextError, addToCartButtonContainer } = this.refs;

    if (this.#timeout) clearTimeout(this.#timeout);
    if (this.#isSubmitting) return;

    const form = this.querySelector('form');
    if (!form) throw new Error('Product form element missing');

    const buttonComponent = addToCartButtonContainer;
    const buttonElement = buttonComponent?.refs?.addToCartButton;

    if (buttonElement?.disabled || buttonElement?.getAttribute('aria-disabled') === 'true') {
      return;
    }

    this.#isSubmitting = true;
    buttonComponent?.disable();

    try {
      const formData = new FormData(form);
      const isProductPage = window.location.pathname.includes('/products/');

      const cartSectionIds = this.#getCartSectionIds();
      if (cartSectionIds.length) {
        formData.append('sections', cartSectionIds.join(','));
      }

      const fetchCfg = fetchConfig('javascript', { body: formData });

      const response = await fetch(Theme.routes.cart_add_url, {
        ...fetchCfg,
        headers: {
          ...fetchCfg.headers,
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        const message = data.message || 'Unable to add this item to cart.';

        this.dispatchEvent(
          new CartErrorEvent(
            form.getAttribute('id') || '',
            message,
            data.description,
            data.errors
          )
        );

        if (addToCartTextError) {
          addToCartTextError.classList.remove('hidden');

          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = message;
          } else {
            addToCartTextError.appendChild(document.createTextNode(message));
          }
        }

        this.#setLiveRegionText(message);

        this.#timeout = window.setTimeout(() => {
          if (addToCartTextError) addToCartTextError.classList.add('hidden');
          this.#clearLiveRegionText();
        }, 10000);

        return;
      }

      if (addToCartTextError) {
        addToCartTextError.classList.add('hidden');
        addToCartTextError.removeAttribute('aria-live');
      }

      this.#setLiveRegionText('Added to cart');

      const id = formData.get('id');
      if (!id) throw new Error('Form ID is required');

      const itemCount =
        Number(formData.get('quantity')) || Number(this.dataset.quantityDefault) || 1;

      history.replaceState(null, '', window.location.pathname);

      this.dispatchEvent(
        new CartAddEvent({}, id.toString(), {
          source: isProductPage ? 'product-form-component' : 'collection-product-form-component',
          itemCount,
          productId: this.dataset.productId,
          sections: data.sections,
        })
      );

      this.#timeout = window.setTimeout(() => {
        this.#clearLiveRegionText();
      }, 3000);
    } catch (error) {
      console.error(error);
      this.#setLiveRegionText('Something went wrong. Please try again.');
    } finally {
      this.#isSubmitting = false;
      buttonComponent?.enable();
      cartPerformance.measureFromEvent('add:user-action', event);
    }
  }

  #getCartSectionIds() {
    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const ids = [];

    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        ids.push(item.dataset.sectionId);
      }
    });

    return ids;
  }

  #setLiveRegionText(text) {
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = text;
    }
  }

  #clearLiveRegionText() {
    if (this.refs.liveRegion) {
      this.refs.liveRegion.textContent = '';
    }
  }

  onVariantUpdate(event) {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;
    const currentAddToCartButton = addToCartButtonContainer?.refs?.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector('[ref="addToCartButton"]');

    if (!currentAddToCartButton) return;

    if (event.detail.resource == null || event.detail.resource.available === false) {
      addToCartButtonContainer?.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute('hidden', 'true');
    } else {
      addToCartButtonContainer?.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute('hidden');
    }

    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    if (variantId) {
      variantId.value = event.detail.resource?.id ?? '';
    }

    if (event.detail.resource) {
      const productVariantMedia = event.detail.resource.featured_media?.preview_image?.src;
      if (productVariantMedia) {
        addToCartButtonContainer?.setAttribute(
          'data-product-variant-media',
          `${productVariantMedia}&width=100`
        );
      }

      document.dispatchEvent(
        new CustomEvent('variant:change', {
          detail: { variant: event.detail.resource },
        })
      );
    }
  }

  onVariantSelected() {
    this.refs.addToCartButtonContainer?.disable();
  }
}

if (!customElements.get('product-form-component')) {
  customElements.define('product-form-component', ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  source;
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    if (!this.source || !this.destination) {
      this.remove();
      return;
    }

    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };

    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    const controlPoint1 = {
      x: startPoint.x,
      y: startPoint.y - 200,
    };

    const controlPoint2 = {
      x: endPoint.x - 300,
      y: endPoint.y - 100,
    };

    let startTime = null;
    const duration = 600;
    this.style.opacity = '1';

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;

      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const position = bezierPoint(progress, startPoint, controlPoint1, controlPoint2, endPoint);

      this.style.setProperty('--x', `${position.x}px`);
      this.style.setProperty('--y', `${position.y}px`);
      this.style.setProperty('--scale', `${1 - progress * 0.5}`);

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        this.style.opacity = '0';
        onAnimationEnd(this, () => this.remove());
      }
    };

    requestAnimationFrame(animate);
  }
}

function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  return {
    x: aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x,
    y: aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y,
  };
}

if (!customElements.get('fly-to-cart')) {
  customElements.define('fly-to-cart', FlyToCart);
}